// Deleting a patient captured by mistake (UC-PATIENTS-028; PAT-55, PAT-56, BR-22): the "Delete patient…" dialog on the record
// and Patients › Deleted records at /patients/deleted. The dialog asks for the reason and posts POST /patients/{id}/void with
// the record's If-Match and one Idempotency-Key per reason — a Control PIN action (`Delete`, record type `patient`). A patient
// with history is refused before any PIN, naming what blocks it in words, with merge or archive as the way out. The record is
// voided and kept, never removed. Deleted records lists every voided patient, most recently first, with the deleted ones'
// who, when and why and the merged-away ones' survivor. The prototype has neither: both use the record's and the worklist's
// vocabulary (the lock mark, the warn alert, the table with a pager).
import type { DeletedPatientEntry, PatientHistoryKind, PatientView } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { patients } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime } from "../lib/format";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Chip, Empty, ErrorAlert, Field, Loading, LockMark, Modal, Pager, Panel, TextArea } from "../ui/bits";
import { NoAccessPanel, PageHead } from "./common";
import { patientName, recordPath, useBranchNames, useLeaveGuard } from "./PatientsShared";

/** BR-PATIENTS-037's kinds of history, as the refusal names them. */
export const HISTORY_LABEL: Record<PatientHistoryKind, string> = {
  dependant: "dependants on the account",
  appointment: "appointments",
  bookings_inbox: "Zoho Bookings bookings",
  bookings_contact_link: "a linked Zoho Bookings contact",
  consent_record: "a consent",
  patient_document: "documents",
  patient_registration_submission: "a kiosk registration",
  dsar_request: "a data-subject request",
  patient_merge: "a duplicate merged into it",
  medical_history: "recorded medical history",
  principal_member: "another patient's medical aid naming them as principal member",
};

/** The history the refusal names, in words; null when the error is not that refusal. */
export function historyRefusal(error: unknown): string | null {
  if (!isApiError(error) || error.meta.reason !== "has_history" || !Array.isArray(error.meta.blockingRecordTypes)) return null;
  const kinds = (error.meta.blockingRecordTypes as string[]).map((k) => HISTORY_LABEL[k as PatientHistoryKind] ?? k);
  return kinds.length > 1 ? `${kinds.slice(0, -1).join(", ")} and ${kinds[kinds.length - 1]}` : kinds[0] ?? "history";
}

export function DeleteModal({ p, etag, onClose, onDone }: { p: PatientView; etag: string | null; onClose: () => void; onDone: (entry: DeletedPatientEntry) => void }) {
  const withStepUp = useStepUp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // From Authorise until the answer — several seconds (UC-PATIENTS-028 step 5): said in the dialog, and leaving the page asks first.
  const [working, setWorking] = useState(false);
  useLeaveGuard(working);
  const [error, setError] = useState<unknown>(null);
  // One key per logical request: another reason is another body (BR-TENANCY-013).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]);
  useEffect(() => { setError(null); }, [reason]);
  // The PIN prompt's Escape reaches this dialog too: while the deletion is in flight the dialog stays.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
  const name = `${patientName(p)} (${p.accountCode})`;

  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    if (!etag) { setError(new Error("The record was read without a version; reload and try again.")); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => { if (g.token) setWorking(true); return patients.void(p.id, why, etag, { ...g, idempotencyKey: key }); }, {
        title: `Delete ${p.accountCode}`, reason: "none",
        description: `${name} leaves every search and list and is kept under Deleted records; ${p.accountCode} is never issued again. The reason is written to the Control Log.`,
      });
      if (r) onDone(r.data);
    } catch (err) { setError(err); }
    finally { setBusy(false); setWorking(false); }
  };

  const history = historyRefusal(error);
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  const stale = isApiError(error) && error.code === "VERSION_CONFLICT";
  return (
    <Modal title={`Delete ${patientName(p)}`} meta={<>Owner or Admin · captured by mistake</>} onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy || !reason.trim() || history !== null}>{busy ? "Deleting…" : "Delete patient"}<LockMark /></button>
    </>}>
      {working && <Alert tone="info" role="status"><b>Deleting {p.accountCode}…</b> This takes several seconds. Keep this page open; Deleted records opens when it is done.</Alert>}
      {history !== null
        ? <Alert tone="crit"><b>{p.accountCode} cannot be deleted: it has {history}.</b> Only a record with no history may be deleted. Merge it into the right record (Merge a duplicate…) or archive it (Change status…) instead.</Alert>
        : error !== null && !reasonErr && <Alert tone="crit"><b>Not deleted.</b> {stale ? "The record was changed since you opened it; close this, check the record and try again." : describeError(error)}</Alert>}
      <Alert tone="warn"><b>For a record that should never have existed</b> — a test entry, or a patient captured twice before any visit. {name} leaves every search, list and family and is kept under Deleted records, never removed; <span className="mono">{p.accountCode}</span> is never issued again.</Alert>
      <Field label="Reason" required htmlFor="delete-reason" error={reasonErr} help="Written on the record and to the Control Log with the authoriser">
        <TextArea id="delete-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <div className="help"><LockMark>requires Control PIN</LockMark> — an Owner or Admin authorises the deletion.</div>
    </Modal>
  );
}

const PAGE = 25;
const dash = <span className="faint">—</span>;

/** Patients › Deleted records (UC-PATIENTS-028 A3): nothing is restored from here, and reading it writes no access entry. */
export function PatientsDeleted() {
  const location = useLocation();
  const navigate = useNavigate();
  const { name: branchName } = useBranchNames();
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const list = useQuery({ queryKey: ["patients", "deleted", cursor ?? ""], queryFn: () => patients.deleted({ limit: PAGE, cursor }) });
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const state = location.state as { notice?: string } | null;
  useEffect(() => {
    if (!state?.notice) return;
    setNotice(<Alert tone="ok"><b>{state.notice}</b></Alert>);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.notice]);
  const rows = list.data?.data.items ?? [];
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  const first = cursors.length * PAGE + 1;
  return (
    <section className="screen" id="s-patients">
      <div className="content">
        <PageHead title="Deleted records" sub="Patients deleted as captured by mistake, and duplicates merged into another record — most recently first. Each record is kept, never removed." />
        <div className="toolbar"><Link className="btn sm right" to="/patients">All patients</Link></div>
        {notice}
        {list.isPending && <Loading what="deleted records" />}
        {list.isError && (denied ? <NoAccessPanel page="Deleted records" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel title="Deleted and merged records" meta={rows.length === 0 ? "" : `${first}–${first + rows.length - 1}${list.data.data.nextCursor ? " · more" : ""} · newest first`} tight
            foot={<>
              <span className="small faint">A merged record lives on in the record it was merged into</span>
              <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data!.data.nextCursor!])} />
            </>}>
            {rows.length === 0 ? (
              <Empty title="No patient has been deleted or merged.">A patient captured by mistake is deleted from their record (Delete patient…, Owner or Admin, Control PIN); a duplicate merged into another record is listed here too.</Empty>
            ) : (
              <div className="tbl-scroll">
                <table aria-label="Deleted records">
                  <thead><tr><th>Account</th><th>Name</th><th>Date of birth</th><th>Home branch</th><th>What happened</th><th>When</th><th>By</th><th>Reason</th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{r.accountCode}</td>
                        <td>{r.displayName}</td>
                        <td className="nw">{formatDate(r.dateOfBirth)}</td>
                        <td>{branchName(r.homeBranchId) ?? dash}</td>
                        <td>{r.kind === "Merged" && r.mergedInto
                          ? <><Chip tone="neutral">Merged</Chip> into <Link className="mono" to={recordPath(r.mergedInto.id)}>{r.mergedInto.accountCode}</Link></>
                          : <Chip tone="warn">Deleted</Chip>}</td>
                        <td className="nw">{formatDateTime(r.voidedAt)}</td>
                        <td>{r.voidedBy?.displayName ?? dash}</td>
                        <td>{r.reason ?? dash}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
      </div>
    </section>
  );
}
